// src/pages/Admin/Dashboard/MessagesSection.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getAllMessagesContactForm,
  markMessageContactForm,
  deleteMessageContactForm,
} from "./../../../services/contactformServices";

import ButtonDelete from "../../../components/Button/ButtonDelete";
import ButtonEnvelopeToggle from "../../../components/Button/ButtonEnvelopeToggle";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

const formatDateTime = (d) => {
  const dt = new Date(d);
  return dt.toLocaleString("fr-FR", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MessagesSection = () => {
  const [messages, setMessages] = useState([]);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | unread | read

  const checkUserRole = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.role_id === 4) setIsDemoMode(true);
  };

  const loadMessages = async () => {
    try {
      const data = await getAllMessagesContactForm();
      setMessages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des messages :", error);
    }
  };

  useEffect(() => {
    loadMessages();
    checkUserRole();
  }, []);

  const handleDemoClick = () => isDemoMode;

  const toggleReadStatus = async (messageId) => {
    if (handleDemoClick()) return;
    try {
      const message = messages.find((m) => m.id === messageId);
      if (!message) return;

      await markMessageContactForm(messageId);

      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, read_message: !m.read_message } : m
        )
      );
    } catch (error) {
      console.error("Erreur toggle read:", error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (handleDemoClick()) return;

    const ok = window.confirm("Supprimer ce message ?");
    if (!ok) return;

    try {
      await deleteMessageContactForm(messageId);
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    } catch (error) {
      console.error("Erreur lors de la suppression du message :", error);
    }
  };

  const unreadCount = useMemo(
    () => messages.filter((m) => !m.read_message).length,
    [messages]
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();

    return [...messages]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .filter((m) => {
        if (filter === "unread" && m.read_message) return false;
        if (filter === "read" && !m.read_message) return false;

        if (!needle) return true;
        const hay = `${m.name || ""} ${m.email || ""} ${m.message || ""}`.toLowerCase();
        return hay.includes(needle);
      });
  }, [messages, q, filter]);

  return (
    <div className="w-full">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-slate-900 dark:text-white">
            Messages
          </div>
          <span
            className={cn(
              "text-xs font-semibold px-2 py-1 rounded-full",
              unreadCount > 0
                ? "bg-rose-500/10 text-rose-700 dark:text-rose-300"
                : "bg-slate-500/10 text-slate-700 dark:text-slate-300"
            )}
          >
            {unreadCount} non lus
          </span>

          {isDemoMode ? (
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-500/10 text-amber-800 dark:text-amber-300">
              Mode démo : actions désactivées
            </span>
          ) : null}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (nom, email, contenu)…"
            className="w-full sm:w-80 px-3 py-2 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-950/40 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/30"
          />

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full sm:w-48 px-3 py-2 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-950/40 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            <option value="all">Tous</option>
            <option value="unread">Non lus</option>
            <option value="read">Lus</option>
          </select>

          <button
            onClick={loadMessages}
            className="w-full sm:w-auto px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 transition"
          >
            Recharger
          </button>
        </div>
      </div>

      {/* Empty */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300/70 dark:border-slate-700 p-8 text-center">
          <div className="text-sm font-semibold text-slate-900 dark:text-white">
            Aucun message
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Ajuste la recherche / filtre, ou reviens plus tard.
          </div>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="grid grid-cols-1 gap-3 md:hidden">
            {filtered.map((m) => (
              <div
                key={m.id}
                className={cn(
                  "rounded-2xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-950/30 p-4",
                  "hover:shadow-sm transition"
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-slate-900 dark:text-white truncate">
                        {m.name || "Sans nom"}
                      </div>
                      <span
                        className={cn(
                          "text-xs font-semibold px-2 py-1 rounded-full",
                          m.read_message
                            ? "bg-slate-500/10 text-slate-700 dark:text-slate-300"
                            : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                        )}
                      >
                        {m.read_message ? "Lu" : "Nouveau"}
                      </span>
                    </div>

                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {formatDateTime(m.created_at)} • {m.email}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <ButtonEnvelopeToggle
                      isRead={m.read_message}
                      onClick={() => toggleReadStatus(m.id)}
                    />
                    <ButtonDelete onClick={() => handleDeleteMessage(m.id)} />
                  </div>
                </div>

                <div className="mt-3 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">
                  {m.message}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
              <thead>
                <tr className="text-left">
                  <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Date
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Contenu
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {filtered.map((m) => (
                  <tr
                    key={m.id}
                    className={cn(
                      "hover:bg-slate-50 dark:hover:bg-slate-900/40 transition",
                      m.read_message
                        ? "bg-slate-50/50 dark:bg-slate-900/20"
                        : "bg-white/70 dark:bg-slate-950/20"
                    )}
                  >
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                      {formatDateTime(m.created_at)}
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-slate-900 dark:text-white">
                          {m.name || "Sans nom"}
                        </div>
                        <span
                          className={cn(
                            "text-xs font-semibold px-2 py-1 rounded-full",
                            m.read_message
                              ? "bg-slate-500/10 text-slate-700 dark:text-slate-300"
                              : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                          )}
                        >
                          {m.read_message ? "Lu" : "Nouveau"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {m.email}
                      </div>
                      <div className="text-sm text-slate-700 dark:text-slate-300 mt-2 line-clamp-3 whitespace-pre-line">
                        {m.message}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-right">
                      <div className="inline-flex items-center gap-2">
                        <ButtonEnvelopeToggle
                          isRead={m.read_message}
                          onClick={() => toggleReadStatus(m.id)}
                        />
                        <ButtonDelete onClick={() => handleDeleteMessage(m.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default MessagesSection;
